/**
 * 導入訪談紀錄(REQ-20260806002)。
 *
 * 需求:「必須支援『導入訪談紀錄』功能,一鍵把訪談紀錄中的【印刷成品規格文字】
 * 與【手動導入的印刷商品圖片】直接帶入出貨單與存貨紀錄中,避免會計重複登打。」
 *
 * 規格文字:後端已從訪談內容挑出候選行,點一下就填進品名 / 規格。
 * 圖片:這裡只顯示「這筆訪談有幾張圖」,實際複製發生在進貨單過帳拋轉代管庫存時 ——
 * 出貨單本身沒有圖片欄位,先搬過來也無處可放。
 */
import { useState } from 'react';
import { useInterviewImport } from '../accounting/api';

interface Props {
  customerId: number;
  selectedInterviewId: number | null;
  onSelect: (interviewId: number | null) => void;
  onApplySpec: (spec: string) => void;
  onApplyNotes: (notes: string) => void;
}

export function InterviewImportPicker({
  customerId,
  selectedInterviewId,
  onSelect,
  onApplySpec,
  onApplyNotes,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError, error } = useInterviewImport(customerId, open);
  const items = data?.items ?? [];

  return (
    <div className="border rounded-md p-3 bg-slate-50">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">導入訪談紀錄</span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-blue-600 hover:underline text-sm"
        >
          {open ? '收起' : '展開'}
        </button>
      </div>

      {!open ? null : isLoading ? (
        <div role="status" className="mt-2 text-sm text-slate-500">
          載入中...
        </div>
      ) : isError ? (
        <div role="alert" className="mt-2 text-sm text-red-600">
          載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
        </div>
      ) : items.length === 0 ? (
        <div className="mt-2 text-sm text-slate-500">
          這個客戶目前沒有訪談紀錄。到「客戶 → 訪談」新增後就能在這裡導入。
        </div>
      ) : (
        <ul className="mt-2 grid gap-2">
          {items.map((it) => {
            const selected = selectedInterviewId === it.interviewId;
            return (
              <li
                key={it.interviewId}
                className={`border rounded-md bg-white p-2 text-sm ${
                  selected ? 'ring-2 ring-blue-400' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-700">
                    {it.visitedAt ? it.visitedAt.slice(0, 10) : '（未填訪談日期）'}
                    {it.interviewType ? (
                      <span className="ml-2 text-slate-400 text-xs">{it.interviewType}</span>
                    ) : null}
                    {it.images.length > 0 ? (
                      <span className="ml-2 text-slate-400 text-xs">
                        商品圖片 {it.images.length} 張
                      </span>
                    ) : null}
                    {it.existingOrderNos.length > 0 ? (
                      <span className="ml-2 text-amber-600 text-xs">
                        已建過 {it.existingOrderNos.join('、')}
                      </span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    onClick={() => onSelect(selected ? null : it.interviewId)}
                    className="text-blue-600 hover:underline text-xs whitespace-nowrap"
                  >
                    {selected ? '取消關聯' : '關聯此訪談'}
                  </button>
                </div>

                {it.specCandidates.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {it.specCandidates.map((spec) => (
                      <button
                        key={spec}
                        type="button"
                        onClick={() => onApplySpec(spec)}
                        className="border rounded px-2 py-0.5 text-xs text-slate-700 hover:bg-blue-50 hover:border-blue-400"
                      >
                        {spec}
                      </button>
                    ))}
                  </div>
                ) : null}

                {it.notes ? (
                  <button
                    type="button"
                    onClick={() => onApplyNotes(it.notes ?? '')}
                    className="mt-1 text-blue-600 hover:underline text-xs"
                  >
                    把訪談內容填進備註
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
